import { FiChevronLeft } from 'react-icons/fi';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { Loader } from '@/components/custom/loader';
import usePermission from '@/hooks/usePermission';
import { useSession } from '@/hooks/useSession';
import { GreTable } from './GreTable';
import SchoolTable from './SchoolTable';
import StudentsTable from './StudentsTable';
import { EmptyState } from '@/components/custom/empty-state';
import BarChart from './BarChart';
import InterventionProposal from './InterventionProposal';

export function InterventionReport() {
  const { session } = useSession();
  const token = session?.user?.authToken;
  const navigate = useNavigate();
  const client = useClient();

  const [courses, setCourses] = useState([]);
  const [kpisData, setKpisData] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [knowledgeAreas, setKnowledgeAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });

  const [selectedGre, setSelectedGre] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);

  const filters = useFormik({
    initialValues: {
      'start-date': moment().subtract(30, 'days').format('YYYY-MM-DD'),
      'end-date': moment().format('YYYY-MM-DD'),
      course: null,
      searchTerm: ''
    }
  });

  const handleSort = field => {
    setSortConfig(prev => {
      if (prev.field === field) {
        return {
          field,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }

      return {
        field,
        direction: 'asc'
      };
    });
  };

  const resetSort = () => {
    setSortConfig({ field: null, direction: 'asc' });
  };

  const kpis = [
    {
      id: 1,
      title: 'Desempenho médio',
      value: kpisData && kpisData?.['average_performance'] !== null ? `${kpisData['average_performance']}%` : '--',
      variation: 'report-kpi-card--analytics'
    },

    ...(!selectedGre && !selectedSchool
      ? [
          {
            id: 2,
            title: 'GRE com maior desempenho',
            value: kpisData && kpisData?.['best_gre'] !== null ? kpisData['best_gre']?.name : '--',
            variation: 'report-kpi-card--video'
          },
          {
            id: 3,
            title: 'GRE com menor desempenho',
            value: kpisData && kpisData?.['worst_gre'] !== null ? kpisData['worst_gre']?.name : '--',
            variation: 'report-kpi-card--video'
          }
        ]
      : []),
    ...(selectedGre && !selectedSchool
      ? [
          {
            id: 2,
            title: 'UE com maior desempenho',
            value: kpisData && kpisData?.['best_institution'] !== null ? kpisData['best_institution']?.name : '--',
            variation: 'report-kpi-card--video'
          },
          {
            id: 3,
            title: 'UE com menor desempenho',
            value: kpisData && kpisData?.['worst_institution'] !== null ? kpisData['worst_institution']?.name : '--',
            variation: 'report-kpi-card--video'
          }
        ]
      : [])
  ];

  const getChartData = async () => {
    setLoading(true);
    let url = `reports/intervention_necessity/charts?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;

    if (filters.values.course) {
      url += `&course_ids=${filters.values.course}`;
    }

    try {
      const { data, error } = await client.fetch(url);
      if (error?.message === 'Parâmetros de data inválidos') {
        setChartData([]);
        setLoading(false);
        return;
      }
      setChartData(data);
    } catch (error) {
      const errorMessage = 'Erro ao buscar os dados do gráfico';
      toast.error(errorMessage);
    }
    setLoading(false);
  };

  const getReportData = async () => {
    setLoading(true);

    let url = `/reports/intervention_necessity/`;

    if (!selectedGre && !selectedSchool) {
      url += `regional_ranking?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }
    if (selectedGre && !selectedSchool) {
      url += `regional_ranking/${selectedGre?.['regional_id']}?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }
    if (selectedGre && selectedSchool) {
      url += `institution_ranking/${selectedSchool?.['institution_id']}?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }

    if (filters.values.course) {
      url += `&course_ids=${filters.values.course}`;
    }

    try {
      const { data, error } = await client.fetch(url);
      if (error?.message === 'Parâmetros de data inválidos') {
        toast.error('O intervalo entre a data inicial e final deve ser de no máximo 30 dias');
        setLoading(false);
        setReportData([]);
        setKpisData(null);
        return;
      }
      setReportData(data?.ranking || data?.institutions || data?.students);
      setKpisData(data?.kpis);
      setKnowledgeAreas(data?.knowledge_areas);
    } catch (error) {
      const errorMessage = 'Erro ao buscar os dados do relatório';
      toast.error(errorMessage);
    }
    setLoading(false);
  };

  const fetchCourses = async (url = '/courses/simple') => {
    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error('Falha ao carregar cursos');
    } else {
      setCourses(data.map(item => ({ value: item.id, label: item.title })));
    }
  };

  const handleDownload = () => {
    setIsDownloading(true);

    let url = `${process.env.REACT_APP_DEV_URL}/reports/intervention_necessity/`;

    if (!selectedGre && !selectedSchool) {
      url += `regional_ranking.xlsx?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }
    if (selectedGre && !selectedSchool) {
      url += `regional_ranking/${selectedGre?.['regional_id']}.xlsx?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }
    if (selectedGre && selectedSchool) {
      url += `institution_ranking/${selectedSchool?.['institution_id']}.xlsx?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }

    if (filters.values.course) {
      url += `&course_ids=${filters.values.course}`;
    }

    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Erro ao baixar o relatório');
        }
        return response.blob();
      })
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `relatorio_necessidade_intervencao_${moment().format('YYYYMMDDHHmmss')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success('Relatório baixado com sucesso!');
      })
      .catch(error => {
        console.error('Erro no download:', error);
        toast.error('Erro ao baixar o relatório');
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    getChartData();
    getReportData();
  }, [filters.values['start-date'], filters.values['end-date'], filters.values.course]);

  useEffect(() => {
    filters.setFieldValue('searchTerm', '');
    getReportData();
    resetSort();
  }, [selectedGre, selectedSchool]);

  const getColor = value => {
    if (value > 66) {
      return 'text-[#1CA388]';
    } else if (value > 33 && value <= 66) {
      return 'text-[#FDB462]';
    } else if (value <= 33) {
      return 'text-[#FF726C]';
    }
  };

  const getBadgeClass = value => {
    if (value > 66) {
      return 'badge--success';
    } else if (value > 33 && value <= 66) {
      return 'badge--warning';
    } else if (value <= 33) {
      return 'badge--danger';
    }
  };

  return (
    <div className="report">
      <header className="report__header">
        <h1 className="report__title">
          <button
            className="card__icon-wrapper"
            aria-label="Voltar"
            title="Voltar"
            onClick={() => navigate('/metricas/institucional')}
          >
            <FiChevronLeft className="card__icon" />
          </button>
          Necessidade de Intervenção
        </h1>
        <p className="report__description">Evidencia Cursos em diferentes níveis de risco (baixo, médio e alto), considerando indicadores de necessidades</p>
      </header>

      <main className="report__body">
        <div
          className="report-filter__select"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex gap-3">
            <Select
              openMenuOnFocus
              options={[{ value: null, label: 'Todos os cursos' }, ...courses]}
              className="react-multi-select report-form__select"
              classNamePrefix="react-multi-select"
              placeholder={'Selecione um curso'}
              noOptionsMessage={() => 'Sem opções'}
              components={{
                IndicatorSeparator: () => null,
                ClearIndicator: () => null
              }}
              onChange={e => filters.setFieldValue('course', e.value)}
            />
            <div className="report-form__datepicker">
              <input
                className="report-form__control"
                id="start-date"
                name="start-date"
                type="date"
                value={moment(filters.values['start-date']).format('YYYY-MM-DD')}
                onChange={e => filters.setFieldValue('start-date', e.target.value)}
                title="Data de início"
                aria-label="Data de início"
                min={moment().startOf('year').format('YYYY-MM-DD')}
              />
              <input
                className="report-form__control"
                id="end-date"
                name="end-date"
                type="date"
                value={moment(filters.values['end-date']).format('YYYY-MM-DD')}
                onChange={e => filters.setFieldValue('end-date', e.target.value)}
                min={moment(filters.values['start-date']).format('YYYY-MM-DD')}
                title="Data de fim"
                aria-label="Data de fim"
              />
            </div>
          </div>
        </div>

        {loading && <Loader />}

        {!loading && reportData?.length === 0 && (
          <EmptyState
            type="data"
            text="Não há dados para exibir no gráfico."
            className="my-10"
          />
        )}

        {!loading && reportData?.length > 0 && (
          <div className="flex flex-col gap-2 lg:flex-row">
            <div className="flex min-w-0 flex-col gap-12">
              <div className="report-kpi-card-wrapper">
                {kpis.map(kpi => (
                  <div
                    className={`report-kpi-card ${kpi.variation}`}
                    key={kpi.id}
                  >
                    <h3 className="report-kpi-card__title">{kpi.title}</h3>
                    <p
                      className="report-kpi-card__value"
                      title={kpi.value}
                    >
                      {kpi.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="report-kpi-card-wrapper">
                <div
                  className="report-kpi-card"
                  style={{ width: '500px' }}
                >
                  <BarChart
                    data={chartData?.['performance_by_activity']}
                    title="Taxa de desempenho por atividade"
                  />
                </div>
                <div
                  className="report-kpi-card"
                  style={{ width: '500px' }}
                >
                  <BarChart
                    data={chartData?.['performance_by_area']}
                    title="Desempenho por área de conhecimento"
                  />
                </div>
              </div>

              <InterventionProposal
                data={reportData}
                studentsLevel={!!selectedSchool}
              />

              <div className="report-widget">
                <div className="report-widget__header">
                  <h3 className="report-widget__title">Ranking de criticidade {!selectedGre ? 'de Áreas Conhecimento por GRE' : !selectedSchool ? 'por Unidade Escolar' : 'por Aluno'}</h3>
                  <div className="report-widget__filter">
                    {usePermission('Download de relatórios', 'Relatórios') && (
                      <button
                        className="btn btn--outline"
                        type="button"
                        onClick={handleDownload}
                      >
                        {isDownloading ? 'Baixando...' : 'Download do relatório'}
                      </button>
                    )}
                  </div>
                </div>

                {!selectedGre && !selectedSchool && (
                  <GreTable
                    data={reportData}
                    filters={filters}
                    setSelectedGre={setSelectedGre}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    getColor={getColor}
                    getBadgeClass={getBadgeClass}
                  />
                )}

                {selectedGre && !selectedSchool && (
                  <SchoolTable
                    data={reportData}
                    selectedGre={selectedGre}
                    setSelectedGre={setSelectedGre}
                    setSelectedSchool={setSelectedSchool}
                    filters={filters}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    getColor={getColor}
                    getBadgeClass={getBadgeClass}
                    kpis={knowledgeAreas}
                  />
                )}
                {selectedGre && selectedSchool && (
                  <StudentsTable
                    data={reportData}
                    selectedGre={selectedGre}
                    selectedSchool={selectedSchool}
                    setSelectedGre={setSelectedGre}
                    setSelectedSchool={setSelectedSchool}
                    filters={filters}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    getColor={getColor}
                    getBadgeClass={getBadgeClass}
                    kpis={knowledgeAreas}
                  />
                )}
              </div>
            </div>
            <div className="card mt-0 h-fit w-72 shrink-0 lg:mt-[950px]">
              <h6 className="border-b border-neutral-lightest pb-2 font-semibold text-primary-dark">Como interpretar os resultados</h6>
              <p className="my-3 text-sm text-neutral">O relatório mostra o percentual de aprendizagem nas áreas de conhecimento e no engajamento dos alunos na plataforma. As cores indicam o nível de necessidade de intervenção:</p>
              <p className="border-l-[3px] border-danger pl-2 text-sm text-neutral">
                <b>Crítico:</b> Alta necessidade de intervenção - baixo desempenho (0 a 33%)
              </p>
              <p className="mt-2 border-l-[3px] border-warning pl-2 text-sm text-neutral">
                <b>Moderado:</b> Média necessidade de intervenção - médio desempenho(34 a 66%)
              </p>
              <p className="mt-2 border-l-[3px] border-success pl-2 text-sm text-neutral">
                <b>Preventivo:</b> Baixa necessidade de intervenção - alto desempenho (67% a 100%)
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
